import { useState, useCallback, useEffect } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { z } from "zod";
import { CircularProgress } from "@material-ui/core";
import { useForm } from "storybook/utils/form";
import { Button } from "storybook/components/button";
import { Notification } from "storybook/components/notification";
import { Typography } from "storybook/components/typography";
import { usePatientAuth, emailLinkErrorMessage } from "context/patient-auth";
import { PrivacyLink } from "components/privacy-link";
import { preserveQueryParams, getAuthReturnPath } from "helpers";
import {
  Container,
  PromptText,
  FormContainer,
  TextContainer,
  StyledHeadings,
  ButtonSeparator,
  ButtonsContainer,
} from "pages/styles/login";
import { ProgressContainer } from "pages/styles/login";
import { Row, ContinueButton, BannerContainer } from "./styles";

const patientSchema = z.object({
  patient: z
    .object({
      email: z.string().email({ message: "Please enter a valid email " }),
    })
    .required()
    .strict(),
});

type Patient = z.infer<typeof patientSchema>;

export default function Passwordless() {
  const history = useHistory();
  const {
    isSubmitting,
    setSubmitting,
    emailLinkError,
    isAuthenticated,
    passwordlessError,
    setEmailLinkError,
    sendEmailWithCode,
    setPasswordlessError,
    setEmailPasswordError,
    resendCodeCount,
    setResendCodeCount,
    isResendCodeTimerActive,
    emailAddress,
  } = usePatientAuth();

  const [formState, setFormState] = useState<Patient>({
    patient: {
      email: "",
    },
  });

  const { Form, Input, getFieldState } = useForm<Patient>({
    schema: patientSchema,
    values: formState,
    shouldFocusError: true,
  });

  const fieldError = Boolean(getFieldState("patient.email").error);

  const handleSubmit = useCallback(
    (data: Patient) => {
      setSubmitting(true);
      setPasswordlessError(false);
      setEmailLinkError(undefined);

      setFormState(data);

      setResendCodeCount(0);

      // This method does not return a Promise, therefore we don't know when it finishes.
      // We rely on state updates in the auth context to control what happens next in this component.
      sendEmailWithCode(data.patient.email);
    },
    [sendEmailWithCode, setEmailLinkError, setPasswordlessError, setSubmitting]
  );

  const handleManualSignInClick = useCallback(() => {
    setEmailPasswordError(undefined);
    setEmailLinkError(undefined);

    history.push({
      pathname: "/login/password",
      search: location.search,
    });
  }, [history, setEmailLinkError, setEmailPasswordError]);

  useEffect(() => {
    if (fieldError) {
      setPasswordlessError(false);
    }
  }, [fieldError, setPasswordlessError]);

  useEffect(() => {
    if (isResendCodeTimerActive && emailAddress) {
      history.push({
        pathname: "/login/passwordless/confirm",
        search: location.search,
      });
    }
  }, [isResendCodeTimerActive, emailAddress, history]);

  if (isAuthenticated) {
    const returnPath = getAuthReturnPath();
    return <Redirect to={returnPath || preserveQueryParams("/")} />;
  }

  const isDisabled = isSubmitting && !passwordlessError;

  return (
    <Container $serverError={passwordlessError}>
      <StyledHeadings>
        <Typography
          theme="default"
          variant="h3"
          color="ebonyClay"
          component="h1"
        >
          Get your personalized cancer risk results
        </Typography>
      </StyledHeadings>
      {resendCodeCount > 1 && (
        <BannerContainer>
          <Notification
            theme="default"
            variant="error"
            fullWidth={true}
            title="Enter your email to get your sign-in code."
          />
        </BannerContainer>
      )}
      {!resendCodeCount && emailLinkError && (
        <BannerContainer>
          <Notification
            theme="default"
            variant="warning"
            fullWidth={true}
            title={emailLinkErrorMessage[emailLinkError]}
          />
        </BannerContainer>
      )}
      <TextContainer>
        <Typography
          theme="default"
          variant="subtitle1"
          color="ebonyClay"
          component="p"
        >
          <strong>First time here?</strong> Enter your email to securely begin
          your cancer risk assessment. This helps us personalize your results.
        </Typography>
        <br />
        <Typography
          theme="default"
          variant="subtitle1"
          color="ebonyClay"
          component="p"
        >
          <strong>Been here before?</strong> Enter your email (or unique email
          ID and password) to continue your assessment or start a new one.
        </Typography>
        <PromptText>
          <Typography
            theme="default"
            variant="subtitle1"
            color="ebonyClay"
            component="p"
          >
            <strong>Securely sign in using your Email</strong>
          </Typography>
        </PromptText>
      </TextContainer>
      <FormContainer>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Input
              theme="default"
              type="email"
              label="Email address"
              name="patient.email"
              labelPosition="top"
              alignText="left"
              hideDirty
            />
          </Row>
          <ButtonsContainer>
            <ContinueButton
              variant="contained"
              disabled={isDisabled}
              $isSubmitting={isSubmitting}
            >
              Continue
            </ContinueButton>
            <ButtonSeparator>
              <Typography
                theme="default"
                variant="subtitle2"
                color="ebonyClay"
                component="span"
              >
                Or
              </Typography>
            </ButtonSeparator>
            <Button
              type="button"
              theme="default"
              variant="outlined"
              disabled={isSubmitting}
              onClick={handleManualSignInClick}
            >
              Use Password
            </Button>
            {isSubmitting && (
              <ProgressContainer>
                <CircularProgress size={30} />
              </ProgressContainer>
            )}
          </ButtonsContainer>
          <PrivacyLink />
        </Form>
      </FormContainer>
    </Container>
  );
}
